import { Typography, ListItemButton, Fade } from "@mui/material"
import { ReactElement } from "react"
import { useMatch } from "react-router-dom"

import { useClickNavButton } from "./useClickNavButton"
import { NAV_FADE_TIME_MS } from "../../../../constants/generalConstants"
import { BUTTON_RADIUS_MULTIPLIER } from "../../../../themes/defaultThemeParams"

type NavButtonProps = {
    title: string
    icon: ReactElement
    route: string
    onClickSideEffect?: () => void
    external?: boolean
}

/**
 * Navigation buttons used on the application navigation bar when it is
 * displayed in "vertical" mode (i.e. as a navigation bar listed on the
 * left hand side of the desktop screen) and the nav bar has been expanded.
 *
 * @example
 * < NavButtonVerticalExpanded title="test" icon={Icon}  route="/"/>
 */
const NavButtonVerticalExpanded = ({
    title,
    icon,
    route,
    onClickSideEffect,
    external,
}: NavButtonProps): ReactElement => {
    const { clickNavButton } = useClickNavButton(
        route,
        external,
        onClickSideEffect
    )
    const selected = !!useMatch(route)
    return (
        <Fade in timeout={NAV_FADE_TIME_MS}>
            <ListItemButton
                sx={{
                    textTransform: "none",
                    borderRadius: BUTTON_RADIUS_MULTIPLIER,
                    justifyContent: "flex-start",
                    padding: "12px 16px 14px 24px",
                    marginBottom: 0.5,
                }}
                color="primary"
                selected={selected}
                onClick={clickNavButton}
            >
                {icon}
                <Typography
                    variant="body1"
                    color={selected ? "primary" : "text.primary"}
                    fontWeight={selected ? "bold" : "normal"}
                >
                    {title}{" "}
                </Typography>
            </ListItemButton>
        </Fade>
    )
}

NavButtonVerticalExpanded.defaultProps = {
    onClickSideEffect: undefined,
    external: false,
}

export default NavButtonVerticalExpanded
